import { useMemo } from 'react';
import type { HarmonyEvent, Measure, NoteEvent } from '../../music/piece';
import { barAt, barEnd } from '../../music/piece';
import { mod } from '../../music/pitch';

interface Props {
  notes: NoteEvent[];
  events: HarmonyEvent[];
  measures: Measure[];
  position: number;
  width: number;
  bars?: number;
}

const LINE_CLASS = ['s-ink', 's-rest', 's-rest', 's-rest', 's-rest'];

/**
 * Voice threads: the implied lines under the figuration, drawn as threads.
 * Notes that do not belong to the chord (suspensions, held notes) are marked as knots.
 */
export function Threads({ notes, events, measures, position, width, bars = 4 }: Props) {
  const bar = barAt(measures, position);
  const idx = measures.findIndex((m) => m.bar === bar);
  const firstIdx = Math.max(0, Math.min(idx - 1, measures.length - bars));
  const window = measures.slice(firstIdx, firstIdx + bars);
  const t0 = window[0].start;
  const t1 = window[window.length - 1].start + window[window.length - 1].length;

  const shown = useMemo(() => notes.filter((n) => n.onset < t1 && n.onset + n.dur > t0), [notes, t0, t1]);
  if (!shown.length) return null;
  const lo = Math.min(...shown.map((n) => n.midi)) - 2;
  const hi = Math.max(...shown.map((n) => n.midi)) + 2;
  const height = 180;
  const padX = 8;
  const x = (t: number) => padX + ((t - t0) / (t1 - t0)) * (width - 2 * padX);
  const y = (m: number) => 8 + ((hi - m) / (hi - lo)) * (height - 16);

  // One representative pitch per line per bar, for drawing the thread.
  const lines = new Map<number, Array<{ t: number; m: number }>>();
  for (const b of window) {
    const inBar = shown.filter((n) => n.bar === b.bar);
    const byLine = new Map<number, number>();
    for (const n of inBar) if (n.line !== undefined && !byLine.has(n.line)) byLine.set(n.line, n.midi);
    byLine.forEach((m, line) => {
      const list = lines.get(line) ?? [];
      list.push({ t: b.start + b.length / 2, m });
      lines.set(line, list);
    });
  }

  const chordAt = (t: number) => {
    for (let i = events.length - 1; i >= 0; i--) if (t >= events[i].start - 1e-9) return events[i];
    return events[0];
  };

  return (
    <svg width={width} height={height} viewBox={`0 0 ${width} ${height}`} role="img" aria-label={`Implied voices in bars ${window[0].bar} to ${window[window.length - 1].bar}`}>
      {window.map((b) => (
        <g key={b.bar}>
          <line x1={x(b.start)} x2={x(b.start)} y1={4} y2={height - 4} className="s-grid" />
          <text x={x(b.start) + 3} y={height - 6} className="t-mono">
            {b.bar}
          </text>
        </g>
      ))}
      {[...lines.entries()].map(([line, pts]) => (
        <polyline
          key={line}
          points={pts.map((p) => `${x(p.t)},${y(p.m)}`).join(' ')}
          fill="none"
          className={LINE_CLASS[Math.min(line, 4)]}
          strokeWidth={line === 0 ? 2.4 : 1.2}
          strokeOpacity={line === 0 ? 0.9 : 0.45}
          strokeLinejoin="round"
        />
      ))}
      {shown.map((n, i) => {
        const ev = chordAt(n.onset);
        const foreign = ev && !ev.chord.pcs.includes(mod(n.midi, 12)) && !(ev.pedal && n.midi === ev.pedal.midi);
        const x1 = x(Math.max(n.onset, t0));
        const x2 = x(Math.min(n.onset + n.dur, t1));
        const sounding = position >= n.onset && position < n.onset + n.dur;
        return (
          <rect
            key={i}
            x={x1}
            y={y(n.midi) - 2.5}
            width={Math.max(2, x2 - x1 - 1)}
            height={5}
            rx={2.5}
            className={foreign || n.altered ? 'f-tension' : n.line === 0 ? 'f-ink' : 'f-muted'}
            opacity={sounding ? 1 : 0.55}
          />
        );
      })}
      <line x1={x(position)} x2={x(position)} y1={2} y2={height - 2} className="s-ink" strokeWidth={1} opacity={position >= t0 && position <= t1 ? 1 : 0} />
      {/* bar boundary at the end */}
      <line x1={x(barEnd(measures, window[window.length - 1].bar))} x2={x(barEnd(measures, window[window.length - 1].bar))} y1={4} y2={height - 4} className="s-grid" />
    </svg>
  );
}
